import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";
import { defineMessages, useIntl } from "react-intl";
import { UserContext } from "@/shared/contexts/user.context";
import { getRouteWithSearchParameter } from "@/shared/utils/route/route.utils";
import { IAssessmentInvite } from "@/shared/hooks/initializer/use-initialize-candidate.hook";
import { ITestAnswer, ITestAnswerTimes } from "@/shared/components/charts/types/chartTypes";
import { ITestChoice, QuestionTypes } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import {
  ASSESSMENT_CODE_SEARCH,
  CANDIDATE_ROUTE,
  LOGIN_ROUTE,
  NOT_FOUND_ROUTE,
} from "@/routing/AppRouter/routes.constants";
import { ITest, ILocalizedTestElement } from "@/modules/assessments/types/test.interface";
import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { AssessmentInviteStatus } from "@/modules/assessments/scenes/AssessmentInvites/invites.constants";
import { ICandidateAssessment } from "@/modules/assessments/assessments.types";
import { useNavigationButtonsHook } from "./useNavigationButtons.hook";
import { getLastAnsweredPageIndex } from "../getLastAnsweredElement";
import {
  getIfNextQuestionNewTest,
  getIfTheEndOfTheAssessment,
  getMissedAnswerQuestionsKeys,
  areAllQuestionsAnswered,
  areAllRequiredQuestionsAnswered,
  hasDuplicateMostLeastAnswers,
  getNextQuestionPage,
} from "../utils/candidate-assessment-view.utils";
import { NO_LONGER_AVAILABLE_MESSAGE, REQUIRED_QUESTIONS_MESSAGE } from "../candidate-assessment.constants";
import { getIfNextSectionNewTest, getNextSectionIndex, getQuestionsForTest } from "../utils/section.utils";
import {
  generateRandomAnswersForTest,
  getAlreadyAnsweredIds,
  getAnswerableElementsForTest,
} from "../utils/randomize-answers.util";

const AUTO_ADVANCE_DELAY = 500;

const messages = defineMessages({
  mostLeastSame: {
    id: "candidate.errors.mostLeastSame",
    defaultMessage: "You cannot select the same statement for both Most and Least. Please choose different statements.",
  },
  skipGenerateAnswers: {
    id: "candidate.errors.skipGenerateAnswers",
    defaultMessage: "Error skipping and auto generating answers. Please try again or contact support.",
  },
});

export function useCurrentAnswerHook({
  test,
  invite,
  questionsList,
  assessment,
  answers,
  saveTestElementAnswer,
  currentPage,
  setCurrentPage,
  currentAnswers,
  setCurrentAnswers,
  sectionsTime,
  isTimedOutSection,
}: {
  test?: ITest;
  invite?: IAssessmentInvite;
  assessment?: ICandidateAssessment;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  currentAnswers: ITestChoice[];
  setCurrentAnswers: Dispatch<SetStateAction<ITestChoice[]>>;
  questionsList: ILocalizedTestElement[][];
  answers: ITestAnswer[];
  saveTestElementAnswer: (
    currentAnswers: ITestChoice[],
    { inviteId, testId }: { inviteId: number; testId: number },
    options: { isLast?: boolean; isBack?: boolean; skipAndRandomize?: boolean },
  ) => Promise<ITestChoice[]>;
  isTimedOutSection: boolean;
  sectionsTime: ITestAnswerTimes;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { code } = useParams();
  const { pathname } = useLocation();
  const { user } = useContext(UserContext);
  const [isOpenedUnansweredModal, setIsOpenedUnansweredModal] = useState<boolean>(false);
  const [isOpenedConfirmationModal, setIsOpenedConfirmationModal] = useState<boolean>(false);
  const [isStartingLoading, setIsStartingLoading] = useState<boolean>(false);
  const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);

  // Track which page triggered auto-advance (the page we're leaving)
  const [pageThatAutoAdvanced, setPageThatAutoAdvanced] = useState<number | null>(null);
  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastInputModality = useLastInputModality();

  const { isAllowToGoBack, isAllowToGoNext, canGoBackOneQuestion } = useNavigationButtonsHook({
    test,
    currentPage,
    questionsList,
    currentAnswers,
    pageThatAutoAdvanced,
  });

  // Clean up auto-advance timer on unmount or page change
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, [currentPage]);

  // Latest-ref so the timeout effect can read fresh values without
  // re-firing when they change (we only want to fire on timeout).
  const timeoutSaveRef = useRef({ currentAnswers, currentPage, questionsList, invite, test, saveTestElementAnswer });
  timeoutSaveRef.current = { currentAnswers, currentPage, questionsList, invite, test, saveTestElementAnswer };

  // Persist answers and, when applicable, mark the test as done on section timeout.
  useEffect(() => {
    if (!isTimedOutSection) {
      return;
    }

    const { currentAnswers, currentPage, questionsList, invite, test, saveTestElementAnswer } = timeoutSaveRef.current;

    if (!invite?.inviteId || !test?.testId) {
      return;
    }

    const isLastSectionInTest =
      getIfNextQuestionNewTest(questionsList, currentPage) ||
      getIfNextSectionNewTest(questionsList, currentPage, test.testId) ||
      getIfTheEndOfTheAssessment(questionsList, currentPage, test.testId);

    (async () => {
      await saveTestElementAnswer(
        currentAnswers,
        { inviteId: invite.inviteId, testId: test.testId },
        { isLast: isLastSectionInTest },
      );
    })();
  }, [isTimedOutSection]);

  // Eventually should move this to use ref(s)
  const scrollAndFocusFirstTestElement = useCallback(() => {
    const upperElement = document.getElementById("first");
    if (upperElement) {
      upperElement.scrollIntoView({ behavior: "smooth", block: "start" });
      const firstTestElement = document.querySelector('[id^="test-element-"]') as HTMLElement;
      firstTestElement?.focus();
    }
  }, []);

  const saveAnswers = async (
    {
      inviteId,
      isLast = false,
      isBack = false,
    }: {
      inviteId: number;
      isLast?: boolean;
      isBack?: boolean;
    },
    isConfirmed = false,
  ) => {
    if (!test?.testId) {
      return;
    }

    setIsLoadingButton(true);

    try {
      if (!isTimedOutSection || isLast || (isConfirmed && !isTimedOutSection)) {
        await saveTestElementAnswer(currentAnswers, { inviteId, testId: test?.testId }, { isLast, isBack });
      }

      setCurrentPage((prevState) => {
        const nextPage = getNextQuestionPage(prevState, questionsList, answers);

        return isBack ? prevState : nextPage;
      });
    } finally {
      navigate(pathname.split("?")[0]);
      setIsLoadingButton(false);
      scrollAndFocusFirstTestElement();
    }
  };

  const handleChoiceSelect = useCallback(
    (value: ITestChoice) => {
      // Change shouldn't fire for timed out sections, but include in guard just in case
      if (!invite || !test || isTimedOutSection) {
        return;
      }

      setCurrentAnswers((prevState) => {
        const hasAnswer = prevState.some((answer) => answer.elementId === value.elementId);
        const newAnswers = !hasAnswer
          ? [...prevState, value]
          : [...prevState.filter((answer) => answer.elementId !== value.elementId), value];

        if (autoAdvanceTimerRef.current) {
          clearTimeout(autoAdvanceTimerRef.current);
          autoAdvanceTimerRef.current = null;
        }

        if (
          lastInputModality.current !== "keyboard" &&
          checkPageAutoAdvanceEligibility({ test, questionsList, currentPage })
        ) {
          autoAdvanceTimerRef.current = setTimeout(async () => {
            try {
              await saveTestElementAnswer(
                newAnswers,
                { inviteId: invite.inviteId, testId: test.testId },
                { isBack: false },
              );

              const nextPage = getNextQuestionPage(currentPage, questionsList, answers);
              setPageThatAutoAdvanced(currentPage);
              setCurrentPage(nextPage);
              scrollAndFocusFirstTestElement();
            } catch (error) {
              console.error("Error auto-advancing:", error);
            }
          }, AUTO_ADVANCE_DELAY);
        }

        return newAnswers;
      });
    },
    [
      setCurrentAnswers,
      questionsList,
      currentPage,
      invite,
      test,
      saveTestElementAnswer,
      answers,
      setCurrentPage,
      lastInputModality,
      isTimedOutSection,
      scrollAndFocusFirstTestElement,
    ],
  );

  const goToNextTest = () => {
    if (!currentPage) {
      return;
    }

    const nextPage = getNextQuestionPage(currentPage, questionsList, answers, true);
    if (nextPage < 1 || (nextPage <= questionsList.length && !questionsList[nextPage - 1])) {
      return;
    }

    setCurrentPage(nextPage);
    scrollAndFocusFirstTestElement();
  };

  const goToFinishAssessment = () => {
    setCurrentPage(questionsList.length + 1);
  };

  const goToNextSection = () => {
    if (!currentPage) {
      return;
    }

    const nextSectionIndex = getNextSectionIndex(questionsList, currentPage);
    if (nextSectionIndex < 0 || !questionsList[nextSectionIndex]) {
      return;
    }

    setCurrentPage(nextSectionIndex + 1);
    scrollAndFocusFirstTestElement();
  };

  /**
   * @param toLastUnansweredPage - if true, go to the earliest page with *no* answered questions, even if the questions missing are required. This is needed for tests that have the back button disabled.
   */
  const goBackToUnanswered = (toLastUnansweredPage = false) => {
    const missedPageIndexes = getMissedAnswerQuestionsKeys(questionsList, answers, test?.testId);
    let missedPageIndex = missedPageIndexes.at(0); // this represents the *first* page with a missing answer

    if (toLastUnansweredPage && invite?.testAnswers) {
      const testAnswer = answers.find((answer) => answer.testId === test?.testId);

      if (testAnswer) {
        const lastAnsweredPageIndex = getLastAnsweredPageIndex(questionsList, testAnswer);

        if (lastAnsweredPageIndex >= 0) {
          // +1 to move to the next page after the last answered
          missedPageIndex = lastAnsweredPageIndex + 1;
        }
      }
    }

    if (!missedPageIndexes.length) {
      // Go to the last page of the "current" test. This will force users who have timed out but not completed a test to
      // acknowledge that they're done with the test.

      // lastIndexOf isn't available in TS v4
      missedPageIndex = questionsList.length - 1;
      for (let i = questionsList.length - 1; i >= 0; i--) {
        if (questionsList[i][0].testId === test?.testId) {
          missedPageIndex = i;
          break;
        }
      }
    }

    goToPageIndex(missedPageIndex);
  };

  const goToPageIndex = (pageIndex?: number) => {
    if (typeof pageIndex !== "number") {
      return;
    }

    const page = pageIndex + 1;

    if (currentPage === page || !questionsList[pageIndex]) return;

    const elementKey = questionsList[pageIndex][0]?.config.key; // first element on the page, not necessarily the unanswered one
    const route = getRouteWithSearchParameter(pathname, ASSESSMENT_CODE_SEARCH, elementKey);

    setCurrentPage(page);
    navigate(route);
  };

  const handleBackClick = async () => {
    if (!invite || !test?.testId) {
      return;
    }

    const canGoBack = test?.config.backButton || canGoBackOneQuestion;

    if (!canGoBack) {
      return;
    }

    setPageThatAutoAdvanced(null); // Clear since we're manually navigating
    setCurrentPage((prevState) => prevState - 1);
    await saveAnswers({ isBack: true, inviteId: invite.inviteId });
  };

  const handleNextClick = async (isConfirmed = false, isBack = false) => {
    if (!invite || !test?.testId || !questionsList) return;

    // Clear auto-advance flag since we're manually navigating
    setPageThatAutoAdvanced(null);

    const currentPageQuestions = questionsList[currentPage - 1].filter(
      ({ config }) => config.type !== QuestionTypes.Text,
    );

    if (hasDuplicateMostLeastAnswers(currentAnswers, currentPageQuestions)) {
      return notification.error({
        message: intl.formatMessage(messages.mostLeastSame),
      });
    }

    // Validate required questions are answered unless section timed out
    if (!isTimedOutSection && !areAllRequiredQuestionsAnswered(currentAnswers, questionsList, currentPage)) {
      return notification.error({
        message: intl.formatMessage(REQUIRED_QUESTIONS_MESSAGE),
      });
    }

    // when not allowed to go back and has unanswered questions on current page
    // 1. show confirmation modal
    // 2. continue / cancel
    if (
      !isConfirmed &&
      !test?.config.backButton &&
      !areAllQuestionsAnswered(currentAnswers, questionsList, currentPage)
    ) {
      return setIsOpenedConfirmationModal(true);
    }

    const ifNextQuestionNewTest = getIfNextQuestionNewTest(questionsList, currentPage);
    const missedKeys = getMissedAnswerQuestionsKeys(questionsList, answers, test.testId, false);

    const lastMissedKey = missedKeys.at(-1);
    const isLastMissedTestPage = lastMissedKey === currentPage - 1;
    // if last missed answers on the current page ->
    //   check that current answers equal to current page questions length (means that all questions answered)
    //   if yes (all questions answered), delete key from missing
    if (
      currentAnswers.filter((answer) => answer.choiceKey).length === currentPageQuestions.length &&
      isLastMissedTestPage
    ) {
      missedKeys.splice(-1, 1);
    }

    // when allow to go back and has unanswered questions on current test
    // 1. show confirmation modal
    // 2. continue (ignore) / confirm to go back to first unanswered
    if (ifNextQuestionNewTest && !isConfirmed && test?.config.backButton && missedKeys.length) {
      return setIsOpenedUnansweredModal(true);
    }

    if ((!missedKeys.length && !isTimedOutSection) || isLastMissedTestPage) {
      isConfirmed = true;
    }

    if (isLoadingButton) {
      return;
    }

    const isBackAllowed = isBack && test?.config.backButton;

    const isLastPage = questionsList.length === currentPage;
    const isLast = (ifNextQuestionNewTest || isLastPage) && (isConfirmed || isTimedOutSection);
    await saveAnswers(
      { isLast: isLast && !isBackAllowed, isBack: isBackAllowed, inviteId: invite.inviteId },
      isConfirmed,
    );
  };

  /**
   * Skips the current test by randomizing answers for all unanswered questions,
   * preserving any selections the user already made. Marks the test as complete
   * and navigates to the next test (or finish screen).
   */
  const handleSkipClick = async () => {
    if (!test || !invite) {
      return;
    }

    const currentPageQuestions = questionsList[currentPage - 1].filter(
      ({ config }) => config.type !== QuestionTypes.Text,
    );

    if (hasDuplicateMostLeastAnswers(currentAnswers, currentPageQuestions)) {
      return notification.error({
        message: intl.formatMessage(messages.mostLeastSame),
      });
    }

    setIsSkipLoading(true);

    try {
      const questionsForTest = getQuestionsForTest(questionsList, test.testId);
      const answerableElements = getAnswerableElementsForTest(questionsForTest, sectionsTime);
      const persistedAnswers = answers.find((a) => a.testId === test.testId)?.answer || [];
      const elementsAlreadyAnswered = getAlreadyAnsweredIds(persistedAnswers, currentAnswers);

      const userSelections = currentAnswers.filter((a) => a.choiceKey);
      const randomizedAnswers = generateRandomAnswersForTest(answerableElements, elementsAlreadyAnswered);
      const allAnswersToSave = [...userSelections, ...randomizedAnswers];

      await saveTestElementAnswer(
        allAnswersToSave,
        { inviteId: invite.inviteId, testId: test.testId },
        { isLast: true, skipAndRandomize: true },
      );

      setCurrentAnswers([]);
      goToNextTest();
    } catch {
      notification.error({
        message: intl.formatMessage(messages.skipGenerateAnswers),
      });
    } finally {
      setIsSkipLoading(false);
    }
  };

  const handleSubmitClick = async () => {
    if (!user || !user.userId) {
      navigate(getRouteWithSearchParameter(`/${CANDIDATE_ROUTE}/${LOGIN_ROUTE}`, ASSESSMENT_CODE_SEARCH, String(code)));
      return false;
    }

    if (invite && invite.status === AssessmentInviteStatus.InProgress && assessment?.assessmentTests) {
      goBackToUnanswered(!test?.config.backButton);
      return true;
    } else {
      setIsStartingLoading(true);

      if (invite?.status === AssessmentInviteStatus.Rescinded) {
        navigate(`/${NOT_FOUND_ROUTE}`);

        notification.error({
          message: intl.formatMessage(NO_LONGER_AVAILABLE_MESSAGE),
        });
        return false;
      }

      if (invite?.inviteId && user?.userId) {
        try {
          await InvitesHttpService.startAssessment(invite.inviteId);
        } catch (err: any) {
          if (err.response.data.message.includes(AssessmentInviteStatus.Rescinded)) {
            navigate(`/${NOT_FOUND_ROUTE}`);

            notification.error({
              message: intl.formatMessage(NO_LONGER_AVAILABLE_MESSAGE),
            });
            return false;
          } else {
            throw err;
          }
        }
      }

      setIsStartingLoading(false);
      setCurrentPage(1);
      navigate(pathname, { replace: true });

      return true;
    }
  };

  return {
    handleBackClick,
    handleNextClick,
    handleSkipClick,
    handleSubmitClick,
    handleChoiceSelect,
    goToNextTest,
    goToNextSection,
    goToFinishAssessment,
    goBackToUnanswered,
    isStartingLoading,
    isOpenedConfirmationModal,
    setIsOpenedConfirmationModal,
    isOpenedUnansweredModal,
    setIsOpenedUnansweredModal,
    isLoadingButton,
    isSkipLoading,
    isAllowToGoBack,
    isAllowToGoNext: isAllowToGoNext || isTimedOutSection,
    canGoBackOneQuestion,
    pageThatAutoAdvanced,
  };
}

function useLastInputModality(): MutableRefObject<"keyboard" | "pointer" | "unknown"> {
  const last = useRef<"keyboard" | "pointer" | "unknown">("unknown");

  useEffect(() => {
    const onKeyDown = () => (last.current = "keyboard");
    const onPointerDown = () => (last.current = "pointer");
    const onMouseDown = () => (last.current = "pointer");
    const onTouchStart = () => (last.current = "pointer");

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("touchstart", onTouchStart, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("touchstart", onTouchStart, true);
    };
  }, []);

  return last;
}

function checkPageAutoAdvanceEligibility({
  test,
  questionsList,
  currentPage,
}: {
  test: ITest;
  questionsList: ILocalizedTestElement[][];
  currentPage: number;
}) {
  if (!test.config.autoAdvance || currentPage <= 0) {
    return false;
  }

  const hasMoreQuestionsInTest = questionsList
    .slice(currentPage)
    .some((page) => page.length > 0 && page[0]?.testId === test.testId);

  if (!hasMoreQuestionsInTest) {
    return false;
  }

  const currentPageQuestions = questionsList[currentPage - 1] || [];
  let trueFalseCount = 0;
  let answerableCount = 0; // not eligible for auto-advance if there is more than one answerable question on a single page
  for (const element of currentPageQuestions) {
    if (element.config.type !== QuestionTypes.Text) {
      answerableCount++;
      if (answerableCount > 1) break;
    }
    if (element.config.type === QuestionTypes.TrueFalse) {
      trueFalseCount++;
      if (trueFalseCount > 1) break;
    }
  }
  return trueFalseCount === 1 && answerableCount === 1;
}
